import { Chip } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { RowCellContent, Table, TableBody } from "@dash/table";
import { Tile } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { Link } from "react-router-dom-v5-compat";

import { BlikCheck, BlikCheckStatus } from "@megaron/cash-rewards-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { Uuid } from "@megaron/uuid";

export type BlikCheckItem = BlikCheck;

type Props = {
  checks: BlikCheckItem[];
  isLoading: boolean;
};

export type ChipProps = {
  color: string;
  variant: "primary" | "secondary" | "outline";
};

const getStatusLabel = (status: BlikCheckStatus): string => {
  const labels: Record<BlikCheckStatus, string> = {
    pending: "Oczekujący",
    outstanding: "Do realizacji",
    cashed: "Zrealizowany",
    cancelled: "Anulowany",
    expired: "Wygasły",
    blocked: "Zablokowany",
    bankRejected: "Odrzucony przez bank",
    pspRejected: "Odrzucony przez PSP",
    processing: "W trakcie",
  };
  return labels[status];
};

export const BlikChecksTable: React.FC<Props> = ({ checks, isLoading }) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();

  const getChipProps = (status: BlikCheckStatus): ChipProps => {
    if (status === "cashed") return { color: theme.colors.success, variant: "secondary" };
    if (status === "bankRejected" || status === "pspRejected" || status === "cancelled" || status === "blocked")
      return { color: theme.colors.danger, variant: "secondary" };
    if (status === "outstanding" || status === "processing")
      return { color: theme.colors.warning, variant: "secondary" };
    return { color: theme.colors.default, variant: "outline" };
  };

  const tableColumns = [
    {},
    ...tableHeadingList.map(
      ({ heading }) =>
        ({
          isSortable: false,
          label: heading,
        }) as const,
    ),
  ];

  const getTableRowCellsContent = (check: BlikCheckItem): RowCellContent[] => {
    const statusChip = getChipProps(check.status);

    return [
      {
        isLink: false,
        element: (
          <div
            css={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        ),
      },
      {
        isLink: false,
        element: <div css={{ display: "flex", alignItems: "center", gap: 4 }}>{check.id}</div>,
      },
      {
        isLink: false,
        element: (
          <>
            {check.createdAt.toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </>
        ),
      },
      {
        isLink: false,
        element: check.refreshedAt ? (
          <>
            {check.refreshedAt.toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </>
        ) : (
          <span css={{ color: theme.colors.default }}>—</span>
        ),
      },
      {
        isLink: false,
        element: <span>{check.amount.toString()} PLN</span>,
      },
      {
        isLink: false,
        element: (
          <Chip color={statusChip.color} variant={statusChip.variant}>
            {getStatusLabel(check.status)}
          </Chip>
        ),
      },
      {
        isLink: false,
        element: (
          <Link
            to={getMegaraxPath.uri("megarax://crm/customer/{id}", { id: Uuid(check.userId) })}
            css={{
              color: theme.colors.primary,
              textDecoration: "underline",
            }}
          >
            {check.userId}
          </Link>
        ),
      },
    ];
  };

  if (isMobile) {
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "0.625rem", width: "100%", marginBottom: "1rem" }}>
        {checks.map((check) => {
          const statusChip = getChipProps(check.status);

          return (
            <Tile key={check.id}>
              <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", overflow: "hidden" }}>
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span css={{ fontWeight: 600, color: theme.colors.primary }}>Nr referencyjny:</span>
                  <span css={{ color: theme.colors.primary }}>{check.id}</span>
                </div>
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <span css={{ fontWeight: 600 }}>Status:</span>
                  <Chip color={statusChip.color} variant={statusChip.variant}>
                    {getStatusLabel(check.status)}
                  </Chip>
                </div>
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span css={{ fontWeight: 600 }}>Kwota:</span>
                  <span>{check.amount.toString()} PLN</span>
                </div>
                <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span css={{ fontWeight: 600 }}>Utworzono:</span>
                    <span css={{ fontSize: "0.875rem" }}>
                      {check.createdAt.toLocaleString("pl-PL", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  {check.refreshedAt && (
                    <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span css={{ fontWeight: 600 }}>Odświeżono:</span>
                      <span css={{ fontSize: "0.875rem" }}>
                        {check.refreshedAt.toLocaleString("pl-PL", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  )}
                </div>
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span css={{ fontWeight: 600 }}>ID użytkownika:</span>
                  <Link
                    to={getMegaraxPath.uri("megarax://crm/customer/{id}", { id: Uuid(check.userId) })}
                    css={{
                      color: theme.colors.primary,
                      textDecoration: "underline",
                    }}
                  >
                    {check.userId}
                  </Link>
                </div>
              </div>
            </Tile>
          );
        })}
      </div>
    );
  }

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table columns={tableColumns} css={{ padding: "0 2rem" }}>
        <TableBody
          isLoading={isLoading}
          rows={checks.map((check) => ({
            uuid: check.id,
            cellsContent: getTableRowCellsContent(check),
          }))}
        />
      </Table>
    </div>
  );
};

const tableHeadingList: { heading: string }[] = [
  { heading: "Nr referencyjny" },
  { heading: "Data utworzenia" },
  { heading: "Ostatnie odświeżenie" },
  { heading: "Kwota" },
  { heading: "Status" },
  { heading: "ID użytkownika" },
];
